<script setup>
  import { ref, computed, watch } from "vue";
  import { usePermissionStore } from "@/stores/usePermissionStore";
  import DeleteConfirmModal from "@/components/Generics/DeleteConfirmModal.vue";
  
  const props = defineProps({
    permissionIds: {
      type: Array,
      required: true,
    },
  });
  
  const emit = defineEmits(["close", "refresh"]);
  const store = usePermissionStore();
  
  const isDeleting = ref(false);
  
  // (mantido) contagem atual do store — pode mudar após deleção/clear
  const selectedCount = computed(() => store.selectedPermissionIds.length);
  
  // ✅ Congela count + IDs (alvo real do delete)
  const frozenCount = ref(0);
  const frozenIds = ref([]);
  
  function freezeSnapshot() {
    const idsFromProps = Array.isArray(props.permissionIds) ? props.permissionIds : [];
    const idsFromStore = Array.isArray(store.selectedPermissionIds) ? store.selectedPermissionIds : [];
  
    // ✅ Prioriza props.permissionIds (porque seu modal recebe isso)
    const ids = idsFromProps.length ? idsFromProps : idsFromStore;
  
    frozenIds.value = [...ids];
    frozenCount.value = frozenIds.value.length;
  }
  
  // ✅ Mantém snapshot atualizado ENQUANTO NÃO está deletando
  watch(
    () => [props.permissionIds, store.selectedPermissionIds],
    () => {
      if (!isDeleting.value) freezeSnapshot();
    },
    { immediate: true, deep: true }
  );
  
  const modalTitle = "Apagar Permissões em Lote";
  
  const messageTitle = computed(() => {
    const n = frozenCount.value || selectedCount.value;
  
    if (n <= 1) return "Você está prestes a apagar 1 permissão selecionada.";
    return `Você está prestes a apagar ${n} permissões selecionadas.`;
  });
  
  const messageSubtitle = computed(() => "Esta ação não pode ser desfeita.");
  
  function buildErrorMessage(error) {
    return (
      error?.response?.data?.message ||
      error?.message ||
      "Ocorreu um erro ao excluir as permissões."
    );
  }
  
  async function onConfirmDelete() {
    // ✅ congela ANTES de deletar (texto + alvo)
    freezeSnapshot();
    isDeleting.value = true;
  
    try {
      if (!frozenIds.value.length) {
        throw new Error("Nenhuma permissão selecionada para exclusão.");
      }
  
      // ✅ garante que o store delete exatamente o snapshot
      store.selectedPermissionIds = [...frozenIds.value];
  
      await store.deleteSelectedPermissions();
    } catch (error) {
      isDeleting.value = false;
      throw new Error(buildErrorMessage(error)); // toast vem do DeleteConfirmModal
    }
  }
  
  function handleSuccess() {
    emit("refresh");
    emit("close");
  }
  </script>
  
  <template>
    <DeleteConfirmModal
      :show="true"
      :title="modalTitle"
      :messageTitle="messageTitle"
      :messageSubtitle="messageSubtitle"
      confirmText="Sim, apagar"
      cancelText="Cancelar"
      loadingText="Apagando..."
      :onConfirm="onConfirmDelete"
      @success="handleSuccess"
      @close="!isDeleting && emit('close')"
    />
  </template>
  